import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authApi } from '../../api/auth';
import { ApiError, errorMessage } from '../../api/client';
import { isChallenge } from '../../api/types';
import { homeFor, useAuth } from '../../auth/AuthContext';
import { useAuthRouteState } from '../../auth/route-state';
import { Alert, Button, PasswordField, TextField } from '../../components/ui';

/** Email + password. Staff and tenant users sign in the same way; the API resolves the account. */
export function SignIn() {
  const navigate = useNavigate();
  const { completeSignIn } = useAuth();
  const { from, notice, email: prefill } = useAuthRouteState();
  const [email, setEmail] = useState(prefill ?? '');
  const [password, setPassword] = useState('');

  const login = useMutation({
    mutationFn: async () => {
      const cleanEmail = email.trim();
      let result;
      try {
        result = await authApi.login(cleanEmail, password);
      } catch (err) {
        // Signed up but never entered the code: send them to the code page instead of an error.
        if (err instanceof ApiError && err.code === 'USER_NOT_CONFIRMED') {
          navigate('/sign-up/confirm', {
            state: { confirm: { email: cleanEmail, destination: null, medium: null } },
          });
          return;
        }
        throw err;
      }
      if (isChallenge(result)) {
        navigate('/sign-in/challenge', { state: { challenge: result, from } });
        return;
      }
      const me = await completeSignIn(result);
      navigate(from ?? homeFor(me), { replace: true });
    },
  });

  return (
    <>
      <h1 className="auth__title">Sign in</h1>
      <p className="auth__lead">Use the email address you signed up with.</p>
      {notice ? <Alert kind="success">{notice}</Alert> : null}
      {login.error ? <Alert kind="error">{errorMessage(login.error)}</Alert> : null}
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          login.mutate();
        }}
        noValidate
      >
        <TextField
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          autoFocus={!prefill}
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <PasswordField
          label="Password"
          name="password"
          autoComplete="current-password"
          autoFocus={Boolean(prefill)}
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <div className="form__row form__row--between">
          <Link to="/forgot-password">Forgot password?</Link>
          <Link to="/sign-in/phone">Sign in with phone</Link>
        </div>
        <Button type="submit" loading={login.isPending} disabled={!email || !password}>
          Sign in
        </Button>
      </form>
      <p className="auth__switch">
        New to Balsam Hub? <Link to="/sign-up">Create an account</Link>
      </p>
    </>
  );
}
